import React, { useState } from "react";
import { useAppDispatch } from "@/redux/store";
import { Modal } from "@/components/Modal";
import { AuthCodeInput as AuthCode } from "@/components/Inputs/AuthCodeInput";
import { fetchConfirmPhoneChange } from "@/redux/profile/settings/confirmPhoneChange/asyncActions";
import { useInterval } from "@/hooks/useInterval";
import { phoneMaskFormat } from "@/utils/form/phoneMask";
import styles from "./ConfirmPhoneModal.module.scss";

interface ConfirmPhoneModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  phone: string;
  onSuccess: () => void;
  onResend: () => Promise<void>;
}

export const ConfirmPhoneModal: React.FC<ConfirmPhoneModalProps> = ({
  isOpen,
  setIsOpen,
  phone,
  onSuccess,
  onResend,
}) => {
  const dispatch = useAppDispatch();
  const [errorMessage, setErrorMessage] = useState("");
  const [hasError, setHasError] = useState(false);
  const [timer, setTimer] = useState(60);
  const [inputKey, setInputKey] = useState(0);

  useInterval(() => {
    setTimer((t) => t - 1);
  }, timer > 0 ? 1000 : null);

  const handleCode = async (code: string) => {
    if (code.length !== 4) return;
    try {
      await dispatch(
        fetchConfirmPhoneChange({ phone, code: Number(code) })
      ).unwrap();
      setHasError(false);
      setErrorMessage("");
      setIsOpen(false);
      onSuccess();
    } catch {
      setHasError(true);
      setErrorMessage("Неверный код");
      setInputKey((k) => k + 1);
    }
  };

  const handleResend = async () => {
    try {
      await onResend();
      setTimer(60);
      setHasError(false);
      setErrorMessage("");
      setInputKey((k) => k + 1);
    } catch {}
  };

  return (
    <Modal
      title="Подтверждение телефона"
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      closeModal={() => setIsOpen(false)}
      mobileContentSize="fixed"
    >
      <div className={styles.content}>
        <p className={styles.description}>
          Введите код, отправленный на номер{" "}
          <span className={styles.phoneNumber}>{phoneMaskFormat(phone)}</span>
        </p>
        <div className={styles.inputsWrapper}>
          <AuthCode
            key={inputKey}
            containerClassName={styles.inputsWrapper}
            length={4}
            inputClassName={`${styles.input} ${hasError ? styles.input_error : ""}`}
            allowedCharacters="numeric"
            onChange={(v) => {
              if (hasError) {
                setHasError(false);
                setErrorMessage("");
              }
              if (v.length === 4) {
                handleCode(v);
              }
            }}
          />
        </div>
        {hasError && errorMessage && (
          <div className={styles.errorMessage}>{errorMessage}</div>
        )}
        <div className={styles.timerContainer}>
          {timer > 0 ? (
            <span className={styles.timer}>
              Повторить через{" "}
              <span className={styles.timerTime}>{timer} секунд</span>
            </span>
          ) : (
            <span className={styles.resend} onClick={handleResend}>
              Отправить повторно
            </span>
          )}
        </div>
      </div>
    </Modal>
  );
};
